import { useCallback } from 'react'
import type { CanvasPosition, MapStyle, TileSize } from '../../types/infrastructure-map'
import type { UseCanvasStatesResult } from './use-canvas-states'
import type { UseMapStyleSettingsResult } from './use-map-style-settings'
import type { UseLayoutCalculationResult } from './use-layout-calculation'

export interface UseMapActionsOptions {
  canvasStates: UseCanvasStatesResult
  styleSettings: UseMapStyleSettingsResult
  layout: UseLayoutCalculationResult
  mapStyle: MapStyle
}

export interface UseMapActionsResult {
  moveNode: (endpointId: string, position: CanvasPosition) => Promise<void>
  resizeTile: (endpointId: string, size: TileSize) => Promise<void>
  recalculateLayout: (rootNodeId?: string | null) => Promise<void>
}

function isValidRootId(value: unknown): value is string | null {
  return typeof value === 'string' || value === null
}

export function useMapActions(options: UseMapActionsOptions): UseMapActionsResult {
  const { canvasStates, styleSettings, layout, mapStyle } = options

  const moveNode = useCallback(
    async (endpointId: string, position: CanvasPosition): Promise<void> => {
      await canvasStates.updateState(endpointId, {
        positionX: position.x,
        positionY: position.y,
      })
    },
    [canvasStates]
  )

  const resizeTile = useCallback(
    async (endpointId: string, size: TileSize): Promise<void> => {
      await canvasStates.updateState(endpointId, { tileSize: size })
    },
    [canvasStates]
  )

  const recalculateLayout = useCallback(
    async (rootNodeId?: string | null): Promise<void> => {
      const validRoot = isValidRootId(rootNodeId)
      const effectiveRootNodeId = validRoot
        ? rootNodeId
        : (styleSettings.settings?.rootNodeId ?? null)
      if (validRoot && rootNodeId !== styleSettings.settings?.rootNodeId) {
        await styleSettings.setRootNode(rootNodeId)
      }
      const result = await layout.calculateAndApply({
        mapStyle,
        rootNodeId: effectiveRootNodeId,
        includeDotsForUntagged: true,
      })
      if (result) {
        await canvasStates.refetch()
      }
    },
    [layout, mapStyle, styleSettings, canvasStates]
  )

  return { moveNode, resizeTile, recalculateLayout }
}
